import React, { FC, ReactElement, useContext, useEffect, useMemo, useReducer, useState } from "react";
//@ts-ignore
import { LocalizationContext } from "react-localize";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusSquare } from "@fortawesome/free-solid-svg-icons";

//types import
import { OwnerInfoRecord } from "Interfaces/OwnerInfo";
import { ScreenType } from "Constants";

//styles
import "./styles.sass";

//Reusable components
import { MvTextOrEdit } from "Generic/MvTextOrEdit";
import ContractsInfo from "./ContractsInfo/ContractsInfo";

//Reducer imports
import { TransferPointsReducer } from "./TransferPointsReducer";
import { Actions, Contract, inputErrors, PageErrors, PageErrorType, ReciepientInformation, ReipientInformationErrors, SelectedContract, TransferPointRequest, TransferPointsState } from "./types";
import ReservationService from "Services/reservation.service";
import MvLoader from "Generic/MvLoader/MvLoader";
import LocalizationService from "Services/localization.service";
import { MvPrimaryBtn } from "Generic/MvPrimaryBtn";
import { MvTermsConditions } from "Generic/MvTermsConditions";
import { Term } from "Interfaces/BookResort";
import { MvConfirmationModal } from "Generic/MvConfirmationModal/MvConfirmationModal";

type Props = {
    token: string;
    lang: string;
    setScreenType(screenType: ScreenType): void;
}

const InitPageErrors: PageErrors = {
    [PageErrorType.lastContractEmpty]: {
        message: 'Please enter details for the previous contract, before attempting to add a new contract',
        isErrored: false
    },
    [PageErrorType.apiError]: {
        message: 'Sorry, something went wrong, please try again later or please contact Owner/Member Services',
        isErrored: false,
    },
    [PageErrorType.duplicateContract]: {
        message: 'Contract id, year and type already selected',
        isErrored: false,
    },
    [PageErrorType.emailError]: {
        message: 'Please enter a valid email address',
        isErrored: false,
    },
    [PageErrorType.apiSubmitError]: {
        message: 'Sorry, we are not able to process the request at this time, please try again',
        isErrored: false,
    },
};

const RecipientErrors: ReipientInformationErrors = {
    [PageErrorType.reipientFirstNameInputError]: {
        message: 'Recipient owner first name cannot be empty',
        isErrored: false
    },
    [PageErrorType.reipientLastNameInputError]: {
        message: 'Recipient owner last name cannot be empty',
        isErrored: false,
    },
    [PageErrorType.reipientOwnerNumberInputError]: {
        message: 'Recipient owner number cannot be empty',
        isErrored: false,
    },
    [PageErrorType.reipientContractIdInputError]: {
        message: 'Recipient contract id cannot be empty',
        isErrored: false,
    },
    [PageErrorType.termsError]: {
        message: 'Terms error',
        isErrored: false,
    },
    [PageErrorType.additionalEmailError]: {
        message: 'Email field cannot be empty',
        isErrored: false,
    },
    [PageErrorType.selectedContractsEmpty]: {
        message: 'Please complete contracts section',
        isErrored: false,
    },
    [PageErrorType.transferPointError]: {
        message: 'Sorry, We cannot transfer more than 20,000 points per usage year, please try again',
        isErrored: false,
    },
    [PageErrorType.contractsPageError]: {
        message: 'Please correct the errors in the contracts section',
        isErrored: false,
    },
    [PageErrorType.twentyThousandPerUsageYearError]: {
        message: 'Please select less than 20,000 points per usage year',
        isErrored: false,
    },
    [PageErrorType.availablePointsPerContractError]: {
        message: 'You have entered more than available points for selected contract',
        isErrored: false,
    },
};

const InitOwnerInfo: OwnerInfoRecord = {
    cieId: 0,
    ownerNumber: "",
    firstName: "",
    middleName: "",
    lastName: "",
    sourceSystem: "",
    memberships: [],
    ownerBenefitLevels: [],
    email: "",
    phone: {
        code: "",
        phoneNumber: ""
    }
};

const InitRecipientInformation: ReciepientInformation = {
    firstName: "",
    lastName: "",
    ownerNumber: "",
    contractId: ""
}

const InitState: TransferPointsState = {
    contracts: [],
    ownerInfo: InitOwnerInfo,
    isloading: true,
    pageErrors: InitPageErrors,
    selectedContracts: [],
    recipientInformation: InitRecipientInformation,
    additionalEmail: "", //TODO: redundant, remove this
    terms: [],
    recipientErrors: RecipientErrors,
}

const TransferPoints: FC<any> = (props: Props): ReactElement => {
    const [reducerState, dispatch] = useReducer(TransferPointsReducer, InitState);
    const { localize } = useContext(LocalizationContext);

    const reservationService: ReservationService = new ReservationService();
    const localizationService: LocalizationService = new LocalizationService();

    useEffect(() => {
        let dataLoading: [Promise<OwnerInfoRecord>, Promise<Contract[]>
        ] = [
            reservationService.getOwnerInfo(),
            reservationService.getTransferPointsContracts()
        ];

        Promise.all(dataLoading).then((result: any[]) => {
            // const contractsWithPoints: Contract[] = result[1].filter((val: Contract) => {
            //     return val.pointsRemaining > 0;
            // });

            const term: string = localize("static.content.transfer-points-acknowledgement-section2-point1");
            const terms: Term[] = [{
                type: "",
                acknowledgementNeeded: "true",
                name: "",
                description: term,
                versionId: "",
                checked: false
            }];
            const ownerInfo: OwnerInfoRecord = result[0];
            const contracts: Contract[] = result[1];
            const contractsWithPoints: Contract[] = contracts.map((val: Contract) => {
                const contract: Contract = {
                    contractNumber: val.contractNumber,
                    contractID: val.contractID,
                    usageYear: val.usageYear,
                    usageType: `${localizationService.getUsageTypeNameFromJahia(val.usageType).value} - ${val.pointsRemaining} Points`,
                    pointsAllocated: val.pointsAllocated,
                    pointsRemaining: val.pointsRemaining,
                    contractInfo: val.contractInfo,
                    isValidforTransferPoints: val.isValidforTransferPoints
                };
                return contract;
            });

            dispatch({
                type: Actions.SETDATA,
                payload: { contracts:  contractsWithPoints, ownerInfo: ownerInfo, terms: terms }
            });
        }).catch((error: any) => {
            // const errMsg = error.message ? `${error.message}` : "Unspecified error";
            const errMsg = error.message ? `${error.message}` : localize('static.content.pwas-search-fatal-error-message');
            console.log(errMsg);

            setTimeout(() => {
                const msg: string = localize("static.content.transfer-points-error-api")
                dispatch({
                    type: Actions.ERROR,
                    payload: { PageErrorType: PageErrorType.apiError, msg:msg }
                });
                
            }, 2000);
        });
    }, []);

    function addAdditionalContracts() {
        /**
         * 1. check if any field in previous contract is empty or not
         * 
         */
        const lastContract: SelectedContract = reducerState.selectedContracts[reducerState.selectedContracts.length - 1];

        if(lastContract) {
            const isEmpty = Object.values(lastContract).some(x => x === null || x === '' || x === 0);
            const errs = {
                contractIdInputError: localize("static.content.transfer-points-error-previous-contract-section-incomplete"),
                usageYearInputError: localize("static.content.transfer-points-error-previous-contract-section-incomplete"),
                usageTypeInputError: localize("static.content.transfer-points-error-previous-contract-section-incomplete"),
                pointsInputError: localize("static.content.transfer-points-error-previous-contract-section-incomplete"),
            }
            if(isEmpty) {
                const key = Object.keys(lastContract).find((key) => lastContract[key as keyof SelectedContract] === null || lastContract[key as keyof SelectedContract] === ''
                                                                    || lastContract[key as keyof SelectedContract] === 0);

                let payload = {
                    key: "",
                    errs: errs
                };
                
                switch(key) {
                    case "contractId":
                        payload.key = "contractId";
                        break;
                    
                    case "usageYear":
                        payload.key = "usageYear";
                        break;
                        
                    case "usageType":
                        payload.key = "usageType";
                        break;

                    case "numberofPointsToTransfer":
                        payload.key = "numberofPointsToTransfer";
                        break;
                }

                dispatch({
                    type: Actions.ADDINPUTERROR,
                    payload: payload
                });

             } else if(Object.values(lastContract.inputErrors).some((error: any) => {
                return error.isErrored;
             })) {
                let payload = {
                    key: "numberofPointsToTransfer",
                    errs: errs
                };
                dispatch({
                    type: Actions.ADDINPUTERROR,
                    payload: payload,
                });
             } else {
                dispatch({
                    type: Actions.ADDEMPTYCONTRACT,
                    payload: {}
                });
             }
        }
    }

    function addContractWithData(contract: SelectedContract, key: number) {
        dispatch({
            type: Actions.ADDCONTRACTWITHDATA,
            payload: { contract: contract, key: key }
        });
    }

    function removeContract(key: number) {
        dispatch({
            type: Actions.DELETECONTRACT,
            payload: { key: key }
        });
    }

    function setErrorMsg() {
        setTimeout(() => {
            const apiError: string = localize("static.content.transfer-points-error-api");
            const obj = {
                previous: localize("static.content.transfer-points-error-previous-contract-section-incomplete"),//
                apiError: localize("static.content.transfer-points-error-api"),//
                email: localize("static.content.transfer-points-error-email"),//
                submitError: localize("static.content.transfer-points-error-api-submit"),//
                recipient: localize("static.content.transfer-points-error-recipient-first-name-empty"),//
                recipient0: localize("static.content.transfer-points-error-recipient-last-name-empty"),//
                recipient1: localize("static.content.transfer-points-error-recipient-owner-number-empty"),//
                recipient2: localize("static.content.transfer-points-error-recipient-contract-id-empty"),//
                terms: localize("static.content.transfer-points-error-terms-required"),//
                additional: localize("static.content.transfer-points-error-additional-email"),//
                contract: localize("static.content.transfer-points-error-contract-fields-empty"),//
                max: localize("static.content.transfer-points-error-max-of-20k-overall"),//
                generic: localize("static.content.transfer-points-error-generic-error-in-contracts"),//
                max1: localize("static.content.transfer-points-error-max-of-20k-contract-section"),//2
                exceeded: localize("static.content.transfer-points-error-exceeded-available-points"),//
                complete: localize("static.content.transfer-points-error-complete-previous-contract"),
                exceeded1: localize("static.content.transfer-points-error-exceeded-available-points-overall"),//
                increments: localize("static.content.transfer-points-error-increments-restriction"),//
                zero: localize("static.content.transfer-points-error-zero-points-entered"),//
                no: localize("static.content.transfer-points-error-no-points-available")//
            }
            
            // const errMsg = {
            //     term: localize("static.content.transfer-points-acknowledgement-section2-point1")
            // }
        console.log("setErrorMsg", obj)
        // dispatch({
        //     type: Actions.SETERRORMSG,
        //     payload: {errObj: obj}
        // });
        }, 1500);
    }

    // function isDisabled(): string {
    //     const lastContract: SelectedContract = reducerState.selectedContracts[reducerState.selectedContracts.length - 1];

    //     if (lastContract) {
    //         const isEmpty = Object.values(lastContract).every(x => x === null || x === '' || x === 0);

    //         if (isEmpty) {
    //             return "add-additional-contracts add-additional-contracts-disabled"
    //         }
    //     }

    //     return "add-additional-contracts";
    // }

    function displayRemoveButton(): boolean {
        // const lastContract: SelectedContract = reducerState.selectedContracts[reducerState.selectedContracts.length - 1];
        // const isEmpty = Object.values(lastContract).some(x => x === null || x === '' || x === 0);

        // if (isEmpty && reducerState.selectedContracts.length <= 1) {
        //     return false;
        // }

        // return true;

        return reducerState.selectedContracts.length > 1;
    }

    /**
     * Function to validate if an owner doesn't
     * exceed more than 20000 points per usage 
     * year
     */

    const validateUsageYearTransferPoints = (contracts: SelectedContract[]): boolean => {
        const usageYearData = {} as any;
        contracts.forEach((contractItem: SelectedContract) => {
            if (usageYearData[contractItem.usageYear]) {
                usageYearData[contractItem.usageYear] += contractItem.numberofPointsToTransfer;
            } else {
                usageYearData[contractItem.usageYear] = contractItem.numberofPointsToTransfer;
            }
        });

        let notValid = false;
        for (const year in usageYearData) {
            const perYearTotalData = usageYearData[year];
            if (perYearTotalData > 20000) {
                notValid = true;
            }
        }
        
        return notValid;
    }

    const validateTotalPointsForEveryContract = (contracts: SelectedContract[]): boolean => {
        /**
         * 1. for each combination of contract id, type, and year in selected contracts, aggregate the points allocated
         * 2. for each entry from point 1, check to see if points entered is greater than points allocated
         */

        let contractsPointsAggregation: SelectedContract[] = [];

        reducerState.selectedContracts.forEach((contractOriginal: SelectedContract) => {
            const foundContract = contractsPointsAggregation.find((contract: SelectedContract) => {
                return contract.contractId === contractOriginal.contractId && contract.usageYear === contractOriginal.usageYear
                    && contract.usageType === contractOriginal.usageType;
            });

            if(foundContract) {
                foundContract.numberofPointsToTransfer += contractOriginal.numberofPointsToTransfer;
            } else {
                contractsPointsAggregation.push(JSON.parse(JSON.stringify(contractOriginal)));
            }
        });

        console.log("contractsPointsAggregation: " + JSON.stringify(contractsPointsAggregation));

        let notValid: boolean = false;

        contractsPointsAggregation.forEach((contract: SelectedContract) => {
            const selectedContract = reducerState.contracts.find((c: Contract) => {
                return c.contractInfo === contract.contractId && c.usageYear === contract.usageYear && c.usageType === contract.usageType;
            });

            if(selectedContract && (contract.numberofPointsToTransfer > selectedContract!.pointsAllocated)) {
                notValid = true;
            }
        });

        return notValid;

        // const pointsAllocatedDataByContracts = {} as any;
        // const noOfPointsByContracts = {} as any;
        // contracts.forEach((contractItem: SelectedContract) => {
        //     const contractId = Number(contractItem.contractId.trim())
        //     if (!pointsAllocatedDataByContracts[contractId]) {
        //         const defaultContracts = reducerState.contracts.find(x => x.contractID);
        //         pointsAllocatedDataByContracts[contractId] = (defaultContracts || { pointsAllocated: 0 }).pointsAllocated;
        //     }
        //     if (noOfPointsByContracts[contractId]) {
        //         noOfPointsByContracts[contractId] += contractItem.numberofPointsToTransfer;
        //     } else {
        //         noOfPointsByContracts[contractId] = contractItem.numberofPointsToTransfer;
        //     }
        // });

        // let notValid = false;
        // for (const id in noOfPointsByContracts) {
        //     const pointsPerContract = noOfPointsByContracts[id];
        //     if (pointsAllocatedDataByContracts[id] < pointsPerContract) {
        //         notValid = true;
        //     }
        // }
        
        // return notValid;
    }

    function onSubmit() {
        const validationsMap: Map<String, Boolean> = isInputValid();

        let isValid: boolean = true;
        validationsMap.forEach((value:Boolean, key: String) => {
            if(value) {
                isValid = false;
            }
        });

        if(isValid) {
            const contracts: SelectedContract[] = reducerState.selectedContracts;
            const primaryEmail: string = reducerState.ownerInfo.email;
            const additionalEmail: string = reducerState.ownerInfo.email;
            console.log('reducerState.ownerInfo', reducerState.ownerInfo);
            const phone: string = reducerState.ownerInfo.phone && reducerState.ownerInfo.phone.hasOwnProperty('phoneNumber') ? reducerState.ownerInfo.phone.phoneNumber : '';

            const recipient: ReciepientInformation = reducerState.recipientInformation;

            const finalContracts = contracts.map((contract: SelectedContract) => {
                return {
                    contractId: contract.contractId,
                    usageYear: contract.usageYear,
                    usageType: contract.usageType,
                    numberofPointsToTransfer: contract.numberofPointsToTransfer
                }
            });


            const transferRequest: TransferPointRequest = {
                email: primaryEmail,
                additionalEmail: additionalEmail,
                phone: phone,
                recipient: recipient,
                contracts: finalContracts
            };

    
            reservationService.submitTransferPoints(transferRequest).then((resp: any) => {
                props.setScreenType(ScreenType.TRANSFER_POINTS_CONFIRMATION);
            })
            .catch((error: any) => {
                const msg: string = localize("static.content.transfer-points-error-api-submit")
                dispatch({
                    type: Actions.ERROR,
                    payload: { PageErrorType: PageErrorType.apiSubmitError, msg: msg }
                })
            });
        } else {
            const errs = {                
                reipientFirstNameInputError: localize("static.content.transfer-points-error-recipient-first-name-empty"),
                reipientLastNameInputError: localize("static.content.transfer-points-error-recipient-last-name-empty"),
                reipientOwnerNumberInputError: localize("static.content.transfer-points-error-recipient-owner-number-empty"),
                reipientContractIdInputError: localize("static.content.transfer-points-error-recipient-contract-id-empty"),
                termsError: localize("static.content.transfer-points-error-terms-required"),
                additionalEmailError: localize("static.content.transfer-points-error-additional-email"),
                selectedContractsEmpty: localize("static.content.transfer-points-error-contract-fields-empty"),
                contractsPageError: localize("static.content.transfer-points-error-generic-error-in-contracts"),
                twentyThousandPerUsageYearError: localize("static.content.transfer-points-error-max-of-20k-contract-section"),
                availablePointsPerContractError: localize("static.content.transfer-points-error-exceeded-available-points"),
            }
            dispatch({
                type: Actions.TOGGLERECIPIENTINFOERROR,
                payload: {validationsMap: validationsMap, errs:errs}
            })
        }
    }

    function isInputValid() : Map<String, Boolean> {
        let validationsMap = new Map<String, Boolean>();

        const firstName: string = reducerState.recipientInformation.firstName;
        validationsMap.set("firstNameEntry", false);
        if(!firstName || firstName === "") {
            validationsMap.set("firstNameEntry", true);
        }

        const lastName: string = reducerState.recipientInformation.lastName;
        validationsMap.set("lastNameEntry", false);
        if(!lastName || lastName === "") {
            validationsMap.set("lastNameEntry", true);
        }

        const ownerNumber: string = reducerState.recipientInformation.ownerNumber;
        validationsMap.set("ownerNumber", false);
        if(!ownerNumber || ownerNumber === "") {
            validationsMap.set("ownerNumber", true);
        }

        const contractId: string = reducerState.recipientInformation.contractId;
        validationsMap.set("ownerContractId", false);
        if(!contractId || contractId === "") {
            validationsMap.set("ownerContractId", true);
        }

        const additionalEmail: string = reducerState.ownerInfo.email;
        validationsMap.set("additionalEmail", false);
        if(!additionalEmail || additionalEmail === "" || reducerState.pageErrors.emailError.isErrored) {
            // return true;
            validationsMap.set("additionalEmail", true);
        }

        validationsMap.set("terms", false);
        if(reducerState.terms.some((term: Term) => {return term.checked === false})) {
            // return true;
            validationsMap.set("terms", true);
        }

        // validationsMap.set("transferPointsError", false);
        // if(validateUsageYearTransferPoints(reducerState.selectedContracts)) {
        //     validationsMap.set("transferPointsError", true);
        // }

        validationsMap.set("validSelectedContracts", false);
        if(doesStateHaveValidContracts()) {
            validationsMap.set("validSelectedContracts", true);
        }

        validationsMap.set("contractsWithError", false);
        if(checkIfSelectedContractsHaveErrors()) {
            validationsMap.set("contractsWithError", true);
        }
        validationsMap.set("twentyThousandPerUsageYearError", false);
        if(validateUsageYearTransferPoints(reducerState.selectedContracts)) {
            validationsMap.set("twentyThousandPerUsageYearError", true);
        }

        validationsMap.set("availablePointsPerContractError", false);
        if (validateTotalPointsForEveryContract(reducerState.selectedContracts)) {
            validationsMap.set("availablePointsPerContractError", true);
        }

        return validationsMap;
    }

    function handleInputBlur(fieldName: string, event: React.FocusEvent<HTMLInputElement>):void {
        dispatch({
            type: Actions.ADDRECIPIENTDATA,
            payload: {fieldName: fieldName, value: event.target.value}
        });
    }

    function handleInputChange(fieldName: string, value: string):void {
        dispatch({
            type: Actions.ADDRECIPIENTDATA,
            payload: {fieldName: fieldName, value: value}
        });
    }

    /**
     * the unique contracts are memoized, since we do not want the component to recalculate the unique contracts for
     * subsequent rerenders
     */
    const memoizedUniqueContracts: string[] = useMemo(() => {
        return Array.from(new Set(reducerState.contracts.map((val: Contract) => {
            return String(val.contractInfo);
        })));
    }, [reducerState.contracts]);
    
    const validateEmail = (email: string): boolean => {
        const re = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
        return email.trim() === '' || re.test(email);
    }
    
    const handleEmailsChange = (e: string) => {

        const validEmail = validateEmail(e);
        if (!validEmail) {
            // set error on the email that was just blurred
            const err:string = localize("static.content.transfer-points-error-email");
            dispatch({
                type: Actions.ADDEMAIL,
                payload: {isErrorEmail: true, err: err}
            });
        } else{
            dispatch({
                type: Actions.ADDEMAIL,
                payload: {email: e}
            });
        }
    }

    const onEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const validEmail = validateEmail(e.target.value);

        if (!validEmail) {
            // set error on the email that was just blurred
            const err:string = localize("static.content.transfer-points-error-email");
            dispatch({
                type: Actions.ADDEMAIL,
                payload: {isErrorEmail: true, err: err}
            });
        } else{
            dispatch({
                type: Actions.ADDEMAIL,
                payload: {email: e.target.value}
            });
        }
    };

    function doesStateHaveValidContracts(): boolean {
        if(reducerState.selectedContracts.length === 0) {
            return true;
        }

        if(reducerState.selectedContracts.length === 1) {
            return Object.values(reducerState.selectedContracts[0]).some(x => x === null || x === '' || x === 0) 
                || Object.values(reducerState.selectedContracts[0].inputErrors).some((x) => x.isErrored);
        }

        return Object.values(reducerState.selectedContracts[reducerState.selectedContracts.length - 1]).some(x => x === null || x === '' || x === 0);
    }

    /**
     * This function is used to check if any of the contract fields have error
     * @returns boolean
     */
    function checkIfSelectedContractsHaveErrors(): boolean {
        let hasError: boolean = false;
        reducerState.selectedContracts.forEach((selectedContract: SelectedContract) => {
            Object.values(selectedContract.inputErrors).forEach((error: any) => {
                if(error.isErrored) {
                    hasError = true;
                }
            });
        });
        return hasError;
    }

    //TODO: Memoize this ????
    function getTermsAndConditions() {
        let jahiaStrings: string[] = [];
        for(let i=1;i<=5;i++) {
            jahiaStrings.push(localize("static.content.transfer-points-acknowledgement-section1-point" + i));
        }

        return jahiaStrings.map((str: string) => {
            return <li>{str.replace("<p>", "").replace("</p>", "").replace("&nbsp;", "")}</li>
        });
    }

    function handleTandCClick() {
        const newTerms = [...reducerState.terms];
        newTerms[0].checked = !newTerms[0].checked;

        dispatch({
            type: Actions.TERMSANDCONDITION,
            payload: {newTerms: newTerms}
        })
    }

    function toggleSubmitErrorModal() {
        dispatch({type: Actions.TOGGLEMODAL, payload: {}});
    }
    
    return (
        <div className="transfer-points-root">
            <MvConfirmationModal 
                header="Error"
                description={reducerState.pageErrors.apiSubmitError.message}
                message=""
                abortLabel=""
                confirmLabel={localize("ok")}
                openModal={reducerState.pageErrors.apiSubmitError.isErrored}
                toggleModal={() => toggleSubmitErrorModal()}
                confirm={() => toggleSubmitErrorModal()}
                abort={() => {}}
            />
            <div className="intro-section">
                <h1>{localize("static.content.transfer-point-main-title")}</h1>
                <h3>{localize("static.content.transfer-point-subhead")}</h3>
                <div className="intro-section-light">
                    <span dangerouslySetInnerHTML={{ __html: localize("static.content.transfer-point-intro-paragraph1") }}></span>
                    <span dangerouslySetInnerHTML={{ __html: localize("static.content.transfer-point-intro-paragraph2") }}></span>
                </div>
            </div>
            <div className="transfer-information">
                <h3>{localize("static.content.transfer-point-transferring-owner-title")}</h3>
            </div>

            {
                reducerState.isloading &&
                <MvLoader show={true} >

                </MvLoader>
            }
            {
                !reducerState.isloading && reducerState.pageErrors.apiError.isErrored &&
                <div className="page-error">
                    <span>{reducerState.pageErrors.apiError.message}</span>
                </div>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored &&
                <div className="owner-information">
                    <div className="owner-information-name">
                        <span>Owner Name</span>
                        <div className="owner-information-name-box">
                            <span>{reducerState.ownerInfo.firstName + " " + reducerState.ownerInfo.lastName}</span>
                        </div>
                    </div>
                    <div className="owner-information-name">
                        <span>Owner Number</span>
                        <div className="owner-information-name-box">
                            <span>{reducerState.ownerInfo.ownerNumber}</span>
                        </div>
                    </div>
                </div>
            }

            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored && reducerState.selectedContracts.length > 0 &&
                reducerState.selectedContracts.map((contract: SelectedContract, index: number) => {
                    return <ContractsInfo
                        contract={reducerState.contracts}
                        key={index}
                        id={index}
                        currentContract={contract}
                        uniqueContracts={memoizedUniqueContracts}
                        displayRemoveButton={displayRemoveButton()}
                        removeContracts={removeContract}
                        addContractWithData={addContractWithData}
                    />;
                })
            }
            {reducerState.recipientErrors[PageErrorType.transferPointError].isErrored && <p className="transfer-point-error">{reducerState.recipientErrors[PageErrorType.transferPointError].message}</p>}

            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored &&
                <div className={"add-additional-contracts"} onClick={addAdditionalContracts}>
                    <span className="add-additional-contracts-text">Add Additional Contracts</span>
                    <FontAwesomeIcon className="add-additional-contracts-icon" icon={faPlusSquare} />
                </div>
            }

            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored &&
                <div className="recipient-root">
                    <div className="transfer-information">
                        <h3 className="recipient-header">{localize("static.content.transfer-point-recipient-owner-title")}</h3>
                    </div>
                    <div className="recipient-information">
                        <div className="owner-information-box">
                            <MvTextOrEdit
                                onChange={(e) => handleInputChange("firstName", e)}
                                onBlur={(e) => handleInputBlur("firstName", e)}
                                value={reducerState.recipientInformation.firstName}
                                placeholder={"Owner First Name"}
                                isEditing={true}
                                hideLabel={false}
                                label={"Owner First Name"}
                                id={'add_owner_first_name'}
                                name={'add_owner_first_name'}
                                hasError={reducerState.recipientErrors[PageErrorType.reipientFirstNameInputError].isErrored}
                                errorMessage={reducerState.recipientErrors[PageErrorType.reipientFirstNameInputError].message}
                                isRequired={true}
                                inputComponent='text'
                            />
                        </div>
                        <div className="owner-information-box">
                            <MvTextOrEdit
                                onBlur={(e) => handleInputBlur("lastName", e)}
                                onChange={(e) => handleInputChange("lastName", e)}
                                value={reducerState.recipientInformation.lastName}
                                placeholder={"Owner Last Name"}
                                isEditing={true}
                                hideLabel={false}
                                label={"Owner Last Name"}
                                id={'add_owner_last_name'}
                                name={'add_owner_last_name'}
                                hasError={reducerState.recipientErrors[PageErrorType.reipientLastNameInputError].isErrored}
                                errorMessage={reducerState.recipientErrors[PageErrorType.reipientLastNameInputError].message}
                                isRequired={true}
                                inputComponent='text'
                            />
                        </div>
                        <div className="owner-information-box">
                            <MvTextOrEdit
                                onBlur={(e) => handleInputBlur("ownerNumber", e)}
                                onChange={(e) => handleInputChange("ownerNumber", e)}
                                value={reducerState.recipientInformation.ownerNumber}
                                placeholder={"Owner Number"}
                                isEditing={true}
                                hideLabel={false}
                                label={"Owner Number"}
                                id={'add_owner_number'}
                                name={'add_owner_number'}
                                hasError={reducerState.recipientErrors[PageErrorType.reipientOwnerNumberInputError].isErrored}
                                errorMessage={reducerState.recipientErrors[PageErrorType.reipientOwnerNumberInputError].message}
                                isRequired={true}
                                inputComponent='text'
                            />
                        </div>
                        <div className="owner-information-box">
                            <MvTextOrEdit
                                onBlur={(e) => handleInputBlur("contractId", e)}
                                onChange={(e) => handleInputChange("contractId", e)}
                                value={reducerState.recipientInformation.contractId}
                                placeholder={"Contract Id"}
                                isEditing={true}
                                hideLabel={false}
                                label={"Contract Id"}
                                id={'add_owner_id'}
                                name={'add_owner_id'}
                                hasError={reducerState.recipientErrors[PageErrorType.reipientContractIdInputError].isErrored}
                                errorMessage={reducerState.recipientErrors[PageErrorType.reipientContractIdInputError].message}
                                isRequired={true}
                                inputComponent='text'
                            />
                        </div>
                    </div>
                </div>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored &&
                    <div className="terms-and-conditions">
                        <h3>{localize("static.content.transfer-points-acknowledgement-section1-heading")}</h3>
                        <ul>{getTermsAndConditions()}</ul>
                        <MvTermsConditions 
                            heading={""}
                            subHeading={""}
                            instructions={reducerState.terms}
                            onChange={() => handleTandCClick()}
                            submissionFailed={reducerState.recipientErrors[PageErrorType.termsError].isErrored}
                        />
                    </div>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored &&
                <div className="owner-email">
                    <MvTextOrEdit
                        onChange={(e) => handleEmailsChange(e)}
                        onBlur={(e) => onEmailBlur(e)}
                        value={reducerState.ownerInfo.email}
                        placeholder={"Your E-mail Address"}
                        isEditing={true}
                        hideLabel={false}
                        label={"Your E-mail Address"}
                        id={'add_owner_email'}
                        name={'add_owner_email'}
                        hasError={reducerState.recipientErrors[PageErrorType.additionalEmailError].isErrored}
                        errorMessage={reducerState.recipientErrors[PageErrorType.additionalEmailError].message}
                        isRequired={true}
                        inputComponent='text'
                    />

                    <div className="intro-section">
                        <div className="intro-section-light">
                            <span dangerouslySetInnerHTML={{ __html: localize("static.content.transfer-point-email-content") }}></span>
                        </div>
                    </div>
                </div>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored && reducerState.recipientErrors[PageErrorType.selectedContractsEmpty].isErrored &&
                    <span className="empty-selected-contracts">{reducerState.recipientErrors[PageErrorType.selectedContractsEmpty].message}</span>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored && reducerState.recipientErrors[PageErrorType.contractsPageError].isErrored &&
                    <span className="empty-selected-contracts">{reducerState.recipientErrors[PageErrorType.contractsPageError].message}</span>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored && reducerState.recipientErrors[PageErrorType.twentyThousandPerUsageYearError].isErrored &&
                <span className="empty-selected-contracts">{reducerState.recipientErrors[PageErrorType.twentyThousandPerUsageYearError].message}</span>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored && reducerState.recipientErrors[PageErrorType.availablePointsPerContractError].isErrored &&
                <span className="empty-selected-contracts">{reducerState.recipientErrors[PageErrorType.availablePointsPerContractError].message}</span>
            }
            {
                !reducerState.isloading && !reducerState.pageErrors.apiError.isErrored &&
                <div className="submit-transfer-point">
                    <MvPrimaryBtn
                        // isDisabled={doesStateHaveValidContracts() || isInputValid()} 
                        isDisabled={false}
                        text={"Submit"}
                        onClick={() => onSubmit()}
                        submissionInProgress={true}
                    />
                </div>
            }
        </div>);
}

TransferPoints.contextTypes = LocalizationContext;
export {
    TransferPoints,
};